/**
 * TeamPhoto: the Directory roster's own avatar, at the view_staff_tools
 * hub floor (teamPhotoUrl) -- NOT CmPhoto's door, which sits at the higher
 * view_workspace floor a line care manager does not always hold. Falls
 * back to initials on no photo or a load error, same contract as CmPhoto.
 *
 * `sm` is the roster table's row avatar; `lg` matches CmPhoto's own "lg"
 * (64px) so a person looks the same size whether their drawer opened from
 * the Directory or from the People page.
 */

import { useState, type ReactNode } from 'react';
import { teamPhotoUrl, type TeamDirectoryPerson } from '@/lib/teamDirectory';
import { initials } from '@/components/workspace/workspaceFormat';

export type TeamPhotoSize = 'sm' | 'lg';

const SIZE_CLASSES: Record<TeamPhotoSize, string> = {
  sm: 'h-7 w-7 text-label',
  lg: 'h-16 w-16 text-base',
};

export function TeamPhoto({
  tenantSlug,
  person,
  size = 'lg',
}: {
  tenantSlug: string;
  person: TeamDirectoryPerson;
  size?: TeamPhotoSize;
}): ReactNode {
  const [errored, setErrored] = useState(false);
  const shared = `inline-flex ${SIZE_CLASSES[size]} flex-shrink-0 items-center justify-center rounded-pill`;
  if (person.has_photo && !errored) {
    return (
      <img
        src={teamPhotoUrl(tenantSlug, person.roster_id)}
        alt={`Photo of ${person.full_name}`}
        loading="lazy"
        className={`${shared} bg-sage-100 object-cover`}
        onError={() => setErrored(true)}
        data-testid="cm-photo-img"
      />
    );
  }
  return (
    <span aria-hidden="true" className={`${shared} bg-sage-100 font-semibold text-sage-800`}>
      {initials(person.full_name)}
    </span>
  );
}
